import { Link } from "react-router-dom";
import { ExternalLink, Newspaper, CalendarDays, Clock, HardHat } from "lucide-react";
import { usePageTitle, Reveal, Eyebrow, RoadDash } from "@/components/bits";
import { QuoteCallout } from "./Home";

const HERO_IMG = "/work/driveway-1.jpg";

export default function News() {
  usePageTitle("News");
  return (
    <>
      <section className="relative isolate overflow-hidden border-b border-white/10" data-testid="news-hero">
        <div
          className="absolute inset-0 bg-cover bg-center opacity-30"
          style={{ backgroundImage: `url(${HERO_IMG})` }}
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-joell-black/75 via-joell-black/60 to-joell-black" aria-hidden="true" />
        <div className="container-x relative pb-16 pt-36 md:pb-20 md:pt-48">
          <Reveal>
            <Eyebrow>News &amp; announcements</Eyebrow>
            <h1 className="mt-4 font-display text-4xl font-bold uppercase tracking-tight text-white sm:text-6xl">News</h1>
            <p className="mt-4 max-w-2xl text-base text-slate-300 sm:text-lg">
              Updates, announcements and milestones from JOELL Asphalt Service &amp; Maintenance Ltd.
            </p>
          </Reveal>
        </div>
        <div className="absolute inset-x-0 bottom-0"><RoadDash /></div>
      </section>

      <section className="container-x py-16 md:py-24" data-testid="news-article-section">
        <Reveal>
          <div className="mx-auto max-w-3xl border border-white/10 bg-joell-surface p-7 sm:p-10" data-testid="news-article">
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-slate-400">
              <span className="flex items-center gap-1.5"><Newspaper size={13} className="text-joell-yellow" /> Ministry of Economy and Labour</span>
              <span className="flex items-center gap-1.5"><CalendarDays size={13} className="text-joell-yellow" /> Published August 16, 2023</span>
              <span className="flex items-center gap-1.5"><Clock size={13} className="text-joell-yellow" /> 3 min read</span>
            </div>

            <h2 className="mt-6 font-display text-2xl font-bold uppercase leading-tight text-white sm:text-3xl">
              Bermudians complete Heavy Highway Construction Asphalt training programme
            </h2>

            <figure className="mt-6" data-testid="news-lead-photo">
              <img
                src="/work/training-programme-team.jpg"
                alt="Graduates of the Heavy Highway Construction Asphalt training programme with Mr. Joell and the training team in front of the JOELL truck"
                loading="lazy"
                className="w-full border border-white/10 object-cover"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                The Heavy Highway Construction Asphalt training programme graduates, with Mr. Joell and the training team
              </figcaption>
            </figure>

            <div className="mt-6 space-y-5 text-sm leading-relaxed text-slate-300 sm:text-base">
              <p>
                The Ministry of Economy and Labour is pleased to announce the successful completion of the Heavy
                Highway Construction Asphalt training programme, a collaborative effort with{" "}
                <span className="font-semibold text-white">Joell&rsquo;s Asphalt Service &amp; Maintenance Ltd.</span> and
                Urban Construction. This comprehensive training initiative has equipped seven (7) young Bermudian men
                with industry-standard National Center for Construction Education and Research (NCCER) credentials,
                marking a significant milestone in Bermuda&rsquo;s commitment to fostering a skilled and capable workforce.
              </p>
              <p>
                The training programme, centered on theoretical knowledge, practical skills, and on-site experience, has
                been developed in close collaboration with internal and external stakeholders within the construction,
                vocation, and trades industry. Participants were immersed in a curriculum that covered a wide range of
                subjects, including heavy truck operation, heavy equipment handling, crane operation, below-grade
                construction, basic asphalt applications, and introduction to various trades such as masonry, drywall,
                roofing, painting, maintenance, landscaping, and asphalt construction paving.
              </p>
              <p>
                Over the course of two months, participants engaged in a variety of learning activities, including
                classroom theory sessions, in-person study groups, self-study, journal writing, interactive lectures,
                quizzes, and NCCER program final exams. The successful completion of the program not only conferred NCCER
                Core Basic Construction Safety Certification but also provided participants with invaluable practical
                skills and soft skills training such as time management, discipline, workplace ethics, character building,
                teamwork, and effective communication.
              </p>
              <p>
                The programme was specifically designed to cater to individuals with diverse entry points, including high
                school graduates with minimal exposure and experience, unemployed individuals, and underemployed
                candidates. Public and private sector industry partners are working collaboratively to secure paid work
                placements and employment opportunities for these trained individuals across small, medium, and large
                businesses within the construction industry.
              </p>
            </div>

            <blockquote className="mt-8 border-l-2 border-joell-yellow pl-5 text-sm italic leading-relaxed text-slate-200 sm:text-base" data-testid="news-quote-minister">
              &ldquo;This initiative is a testament to the Ministry&rsquo;s commitment to cultivating a skilled local
              workforce that meets the demands of the construction industry. By equipping young Bermudians with the
              necessary skills, knowledge, experience, and certifications, the programme ensures that Bermuda maintains
              a competent and capable workforce. Participants are encouraged to explore further employment opportunities
              through the Career Development team at the Department of Workforce Development.&rdquo;
              <footer className="mt-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-joell-yellow not-italic">
                — The Hon. Jason Hayward, JP, MP, Minister of Economy and Labour
              </footer>
            </blockquote>

            <div className="mt-8 border border-white/10 bg-joell-black p-6" data-testid="news-graduates">
              <h3 className="font-display text-sm font-bold uppercase tracking-[0.2em] text-joell-yellow">
                Congratulations to our latest graduates
              </h3>
              <p className="mt-2 text-sm text-slate-400">
                We extend our hearty congratulations to the following young Bermudians who completed and passed the
                Heavy Highway Construction Asphalt training programme and NCCER Certification programme:
              </p>
              <ul className="mt-4 grid grid-cols-1 gap-2.5 sm:grid-cols-2">
                {["Chawki Richardson", "Garsha Daley", "Stevonte Young", "Tyori Lynch", "Quayan Watson", "Samario Valasse", "Justin Cameron"].map((g) => (
                  <li key={g} className="flex items-center gap-2.5 text-sm text-slate-300">
                    <span aria-hidden="true" className="inline-block h-2 w-2 shrink-0 rotate-45 border-t-2 border-r-2 border-joell-green" />
                    {g}
                  </li>
                ))}
              </ul>
            </div>

            <figure className="mt-6" data-testid="news-graduates-photo">
              <img
                src="/work/training-graduates.jpg"
                alt="Graduates of the Heavy Highway Construction Asphalt training programme holding their NCCER certificates"
                loading="lazy"
                className="w-full border border-white/10 object-cover"
              />
              <figcaption className="mt-3 font-mono text-[10px] uppercase tracking-[0.2em] text-slate-400">
                Graduates receiving their NCCER certificates at the training ceremony
              </figcaption>
            </figure>

            <blockquote className="mt-8 border-l-2 border-joell-yellow pl-5 text-sm italic leading-relaxed text-slate-200 sm:text-base" data-testid="news-quote-participant">
              &ldquo;I really appreciated the support and life lessons that were taught along with the technical
              training. The encouragement to help us to develop our own technique and to also be aware of how to be safe
              on the job.&rdquo;
              <footer className="mt-3 font-mono text-[10px] font-bold uppercase tracking-[0.2em] text-joell-yellow not-italic">
                — Samario Valasse, programme participant
              </footer>
            </blockquote>

            <div className="mt-8 space-y-5 text-sm leading-relaxed text-slate-300 sm:text-base">
              <p>
                The Ministry of Economy and Labour would like to express profound gratitude to Mr. Joell, Owner and
                Operator of Joell Asphalt Service &amp; Maintenance Ltd., and Ms. Allanette Hayward Eng. GMICE, along
                with the Training Team at the Department of Workforce Development, for their unwavering endorsement and
                support that led to the resounding success of the Highways Construction Asphalt and NCCER training
                programme.
              </p>
              <p>
                Minister Hayward went on to say, &ldquo;This programme aligns seamlessly with the Ministry&rsquo;s
                Economic Development Strategy, prioritizing investment in people, Bermuda&rsquo;s Youth Employment
                Strategy, National Re-employment Strategy, Training and Development, and Internship Work Placement
                initiatives, all of which foster meaningful engagement between employers and industry stakeholders
                within the workforce.&rdquo;
              </p>
              <p>
                The training and development offered through this programme directly address the demand for skilled and
                certified young Bermudians entering the technical and trades industry. The internship placements
                provided invaluable real-world application opportunities for the acquired skills, bridging the gap
                between theoretical learning and practical application.
              </p>
              <p>
                By empowering our youth with the Construction and NCCER training certificates, this programme
                significantly contributes to reducing unemployment and unskilled labour among Bermudian youth. It marks
                a significant step towards a more prosperous and capable Bermuda.
              </p>
            </div>

            <a
              href="https://bernews.com/2023/08/heavy-highway-construction-asphalt-training/"
              target="_blank"
              rel="noreferrer"
              className="btn-outline mt-10"
              data-testid="news-external-link"
            >
              <ExternalLink size={14} /> Read the announcement as covered by Bernews
            </a>
          </div>
        </Reveal>

        <Reveal delay={0.08}>
          <div className="mx-auto mt-12 flex max-w-3xl flex-col items-start justify-between gap-5 border border-joell-yellow/40 bg-joell-yellow/5 p-7 sm:p-9 md:flex-row md:items-center" data-testid="news-careers-cta">
            <div className="flex items-start gap-4">
              <HardHat size={26} className="shrink-0 text-joell-yellow" />
              <div>
                <h3 className="font-display text-xl font-bold uppercase text-white">Inspired by our team?</h3>
                <p className="mt-1 text-sm text-slate-300">See what it&rsquo;s like to work with JOELL — and apply online today.</p>
              </div>
            </div>
            <Link to="/careers" className="btn-accent !px-6 !py-3" data-testid="news-careers-button">View Careers</Link>
          </div>
        </Reveal>
      </section>

      <QuoteCallout />
    </>
  );
}
